import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { Radio } from '../primitives/Radio';
import { FormField } from '../patterns/FormField';
import { rem } from '../tokens/spacing';

import { rhfInlineError } from './rhfInlineError';
import type { Reaction } from './reactions';
import { useReactions } from './useReactions';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

export interface RhfRadioGroupProps extends RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  options: Array<{ label: string; value: string }>;
  /**
   * Layout direction of the radio options.
   * - `'horizontal'` (default): options side-by-side, wrapping when needed.
   * - `'vertical'`: options stacked top-to-bottom.
   *
   * Set via `adapterProps: { layout: 'vertical' }` on a `FieldDef` or
   * directly on a block-level field mapping.
   */
  layout?: 'horizontal' | 'vertical';
  /**
   * Controls the FormField label position relative to the input.
   * - `'vertical'` (default): label above the radio group.
   * - `'horizontal'`: label beside the radio group (inline label).
   */
  labelOrientation?: 'vertical' | 'horizontal';
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  reactions?: Reaction[];
  /** @deprecated Use `layout` instead. Will be removed in the next DS major. */
  orientation?: 'vertical' | 'horizontal';
  /** @deprecated Use `layout` instead. Will be removed in the next DS major. */
  radioDirection?: 'row' | 'column';
}

export const RhfRadioGroup = React.forwardRef<HTMLDivElement, RhfRadioGroupProps>(
  function RhfRadioGroup(
    {
      control,
      name,
      label,
      options,
      layout = 'horizontal',
      labelOrientation = 'vertical',
      required = false,
      hint,
      tooltip,
      semanticId,
      disabled = false,
      reactions,
      // Legacy — map to new props for backwards compat, but do not expose in docs
      orientation,
      radioDirection,
      controlWidth,
    },
    ref,
  ) {
    const overrides = useReactions(control, reactions);

    const finalDisabled = overrides.disabled ?? disabled;
    const finalRequired = (overrides.required as boolean) ?? required;
    const finalLabel = (overrides.label as string) ?? label;

    if (overrides.visible === false) return null;

    // Legacy prop mapping — old `orientation` controlled both label AND items
    const resolvedLabelOrientation = labelOrientation ?? orientation ?? 'vertical';
    const resolvedLayout: 'horizontal' | 'vertical' =
      radioDirection === 'row' ? 'horizontal'
      : radioDirection === 'column' ? 'vertical'
      : orientation === 'horizontal' ? 'horizontal'
      : layout;

    const isRow = resolvedLayout === 'horizontal';

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

          return (
            <FormField
              ref={ref}
              label={finalLabel}
              hint={hint}
              tooltip={tooltip}
              required={finalRequired}
              orientation={resolvedLabelOrientation}
              labelSpacing={isRow ? 'md' : 'sm'}
              semanticId={semanticId}
              disabled={finalDisabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <div
                role="radiogroup"
                aria-label={finalLabel}
                style={{
                  display: 'flex',
                  flexDirection: isRow ? 'row' : 'column',
                  flexWrap: isRow ? 'wrap' : 'nowrap',
                  gap: isRow ? rem(16) : rem(8),
                }}
              >
                {(options ?? []).map((option) => {
                  const optionId = `${field.name}-${option.value}`.replace(/[^a-zA-Z0-9_-]/g, '_');
                  const checked = String(field.value) === String(option.value);

                  return (
                    <Radio
                      key={option.value}
                      id={optionId}
                      name={field.name}
                      value={option.value}
                      checked={checked}
                      error={Boolean(errorText)}
                      disabled={finalDisabled}
                      onChange={() => {
                        field.onChange(option.value);
                      }}
                      onBlur={field.onBlur}
                    >
                      {option.label}
                    </Radio>
                  );
                })}
              </div>
            </FormField>
          );
        }}
      />
    );
  },
);

RhfRadioGroup.displayName = 'RhfRadioGroup';
